import React, { useEffect, useState } from "react"

import { ExamplePlugin } from "@fuse/plugins/examples/types"
import ExamplePluginsModal from "@fuse/sharedComponents/ExamplePluginsModal"
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline"

import { convertExampleToPlugin } from "@fuse/utils/examplePluginConverter"

import { useFileExplorer } from "../../../recoil/pluginStudio/hooks"
import { useStudioPlugins } from "../../../recoil/pluginStudio/hooks/useStudioPlugins"
import {
  FileExplorerProps,
  FileSystemItem,
  Plugin,
  PluginStructure,
} from "../../../recoil/pluginStudio/types"
import { UserRole } from "../../../recoil/pluginStudio/types"

/**
 * Presentation component that renders the file explorer UI
 */
export const FileExplorerUI: React.FC<FileExplorerProps> = ({
  plugins,
  activePluginId,
  onSelectPlugin,
  onSelectFile,
  activeFilePath = "",
  onCreateNewPlugin,
  showExamplesModal,
  onShowExamplesModal,
}) => {
  // State to track expanded folders
  const [expandedFolders, setExpandedFolders] = useState<
    Record<string, boolean>
  >({})

  // Automatically expand the active plugin and its frontend folder when it changes
  useEffect(() => {
    if (activePluginId) {
      setExpandedFolders((prev) => ({
        ...prev,
        [`/${activePluginId}`]: true,
        [`/${activePluginId}/frontend`]: true,
      }))
    }
  }, [activePluginId])

  // Generate plugin folder structure
  const generatePluginStructure = (plugin: any): PluginStructure => {
    // Get the name from manifest if available, or fallback to plugin.name
    const name = plugin.manifest?.name || plugin.name || "Untitled Plugin"
    const path = `/${plugin.id}`

    return {
      id: plugin.id,
      name,
      path,
      folders: {
        frontend: [
          {
            id: `${plugin.id}-guest-view`,
            name: "GuestView.tsx",
            type: "file",
            role: UserRole.GUEST,
            path: `/${plugin.id}/frontend/GuestView.tsx`,
          },
          {
            id: `${plugin.id}-host-event-view`,
            name: "HostEventView.tsx",
            type: "file",
            role: UserRole.HOST_EVENT_VIEW,
            path: `/${plugin.id}/frontend/HostEventView.tsx`,
          },
          {
            id: `${plugin.id}-host-config-view`,
            name: "HostConfigView.tsx",
            type: "file",
            role: UserRole.HOST_CONFIG_VIEW,
            path: `/${plugin.id}/frontend/HostConfigView.tsx`,
          },
        ],
        backend: [],
        metadata: [
          {
            id: `${plugin.id}-manifest`,
            name: "manifest.json",
            type: "file",
            path: `/${plugin.id}/metadata/manifest.json`,
          },
        ],
      },
    }
  }

  // Ensure plugins is always an array
  const safePlugins = Array.isArray(plugins) ? plugins : []

  // Create plugin structures
  const pluginStructures = safePlugins.map(generatePluginStructure)

  // Toggle folder expansion
  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [path]: !prev[path],
    }))
  }

  // Check if a folder is expanded
  const isFolderExpanded = (path: string): boolean => {
    return expandedFolders[path] === true
  }

  // Render a file item
  const renderFile = (item: FileSystemItem, plugin: PluginStructure) => {
    const isActive = activeFilePath === item.path

    return (
      <div
        key={item.id}
        className={`
          pl-8 py-1 flex items-center text-sm cursor-pointer hover:bg-gray-700/50
          ${isActive ? "bg-blue-600/50 font-semibold" : ""}
        `}
        onClick={() => {
          console.log("[FileExplorer] File clicked:", {
            path: item.path,
            pluginId: plugin.id,
            fileName: item.name,
            isActive: isActive,
          })
          // Find the matching plugin object from the original plugins array
          const pluginObj = safePlugins.find((p) => p.id === plugin.id)
          if (pluginObj) {
            onSelectFile(pluginObj, item.id)
          }
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 mr-2 text-gray-400"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
            clipRule="evenodd"
          />
        </svg>
        {item.name}
      </div>
    )
  }

  // Render a folder
  const renderFolder = (
    name: string,
    path: string,
    items: FileSystemItem[],
    plugin: PluginStructure,
  ) => {
    const isExpanded = isFolderExpanded(path)

    return (
      <div key={path}>
        <div
          className="pl-4 py-1 flex items-center text-sm cursor-pointer hover:bg-gray-700/50"
          onClick={(e) => {
            // Stop the event from propagating to parent elements
            e.stopPropagation()
            toggleFolder(path)
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 mr-2 text-yellow-400 transition-transform ${isExpanded ? "transform rotate-90" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2 text-yellow-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
              clipRule="evenodd"
            />
          </svg>
          {name}
        </div>

        {isExpanded && (
          <div className="ml-2 border-l border-gray-700">
            {items.map((item) => renderFile(item, plugin))}
          </div>
        )}
      </div>
    )
  }

  // Render a plugin
  const renderPlugin = (plugin: PluginStructure) => {
    const isActive = activePluginId === plugin.id
    const isExpanded = isFolderExpanded(plugin.path)

    return (
      <div key={plugin.id} className="mb-1">
        <div
          className={`
            py-2 px-3 flex items-center text-sm cursor-pointer rounded-md
            ${isActive ? "bg-blue-600 text-white" : "hover:bg-gray-700/50"}
          `}
          onClick={() => {
            // Find the matching plugin object from the original plugins array
            const pluginObj = safePlugins.find((p) => p.id === plugin.id)
            if (pluginObj) {
              onSelectPlugin(pluginObj)
            }

            // Toggle folder expansion
            toggleFolder(plugin.path)
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 mr-2 transition-transform ${isExpanded ? "transform rotate-90" : ""}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2 text-blue-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z"
              clipRule="evenodd"
            />
          </svg>
          <span className="truncate">{plugin.name}</span>
        </div>

        {isExpanded && (
          <div className="ml-4 mt-1">
            {renderFolder(
              "frontend",
              `${plugin.path}/frontend`,
              plugin.folders.frontend,
              plugin,
            )}
            {plugin.folders.backend.length > 0 &&
              renderFolder(
                "backend",
                `${plugin.path}/backend`,
                plugin.folders.backend,
                plugin,
              )}
            {renderFolder(
              "metadata",
              `${plugin.path}/metadata`,
              plugin.folders.metadata,
              plugin,
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="w-full h-full bg-gray-800 overflow-auto p-2">
      <div className="mb-4 space-y-2">
        <div
          className="py-2 px-3 flex items-center text-sm cursor-pointer hover:bg-gray-700/50 text-green-400 rounded-md"
          onClick={() => onCreateNewPlugin && onCreateNewPlugin()}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          Create New Plugin
        </div>

        <div
          className="py-2 px-3 flex items-center text-sm cursor-pointer hover:bg-gray-700/50 text-blue-400 rounded-md"
          onClick={() => {
            console.log("[FileExplorer] Browse Examples clicked", {
              onShowExamplesModal,
            })
            onShowExamplesModal && onShowExamplesModal(true)
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Browse Examples
        </div>
      </div>

      {pluginStructures.map(renderPlugin)}

      {safePlugins.length === 0 && (
        <div className="text-center text-gray-500 py-4">
          No plugins found. Create a new one to get started.
        </div>
      )}
    </div>
  )
}

/**
 * Container component that connects to Recoil state
 */
const FileExplorer: React.FC = () => {
  // Replace usePlugins with useStudioPlugins
  const {
    plugins,
    activePluginId,
    selectPlugin,
    loadStudioPlugins,
    addPlugin,
  } = useStudioPlugins()
  const { activeFilePath, selectFile } = useFileExplorer()
  const [showExamplesModal, setShowExamplesModal] = useState(false)

  // Load plugins if needed when component mounts
  useEffect(() => {
    // Only load plugins if there are none (except the default)
    if (plugins.length <= 1) {
      console.log("[FileExplorer] Loading plugins from API")
      loadStudioPlugins().catch((error) => {
        console.error("[FileExplorer] Error loading plugins:", error)
      })
    }
  }, [plugins.length, loadStudioPlugins])

  // Create a new plugin
  const handleCreateNewPlugin = () => {
    // Handle the special case for "new" plugin id
    const newPluginId = "new"
    console.log(`[FileExplorer] Creating new plugin`)
    selectPlugin(newPluginId)
  }

  // Handle example plugin selection
  const handleSelectExample = (example: ExamplePlugin) => {
    console.log(`[FileExplorer] Creating plugin from example: ${example.name}`)

    // Convert example to plugin format
    const plugin = convertExampleToPlugin(example)

    // Add the plugin to the studio
    if (addPlugin) {
      addPlugin(plugin)
    }

    // Select the new plugin
    selectPlugin(plugin.id)

    // Close the modal
    setShowExamplesModal(false)
  }

  const handleSelectPlugin = (plugin: Plugin) => {
    selectPlugin(plugin.id)
  }

  const handleSelectFile = (plugin: Plugin, fileId: string) => {
    selectPlugin(plugin.id)

    // Look for the file in the plugin to get its path and name
    const pluginFile = plugin.files?.find((f: any) => f.id === fileId)
    if (pluginFile) {
      selectFile(pluginFile.path, plugin.id, pluginFile.name)
    } else {
      // Fallback if file not found - use a common pattern from the file structure
      const role = fileId.includes("guest")
        ? UserRole.GUEST
        : fileId.includes("host-event")
          ? UserRole.HOST_EVENT_VIEW
          : fileId.includes("host-config")
            ? UserRole.HOST_CONFIG_VIEW
            : "other"
      const name =
        role === UserRole.GUEST
          ? "GuestView.tsx"
          : role === UserRole.HOST_EVENT_VIEW
            ? "HostEventView.tsx"
            : role === UserRole.HOST_CONFIG_VIEW
              ? "HostConfigView.tsx"
              : fileId
      const path = `/${plugin.id}/frontend/${name}`
      selectFile(path, plugin.id, name)
    }
  }

  return (
    <>
      <FileExplorerUI
        plugins={plugins}
        activePluginId={activePluginId}
        onSelectPlugin={handleSelectPlugin}
        onSelectFile={handleSelectFile}
        activeFilePath={activeFilePath}
        onCreateNewPlugin={handleCreateNewPlugin}
        showExamplesModal={showExamplesModal}
        onShowExamplesModal={setShowExamplesModal}
      />

      {/* Debug info */}
      <div className="p-2 bg-red-100 text-red-800 text-xs">
        Debug: showExamplesModal = {showExamplesModal.toString()}
        <button
          onClick={() => setShowExamplesModal(true)}
          className="ml-2 px-2 py-1 bg-red-500 text-white rounded text-xs"
        >
          Force Open Modal
        </button>
      </div>

      {/* Examples Modal */}
      <ExamplePluginsModal
        isOpen={showExamplesModal}
        onClose={() => setShowExamplesModal(false)}
        onSelectExample={handleSelectExample}
      />
    </>
  )
}

export default FileExplorer
